import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import 'dayjs/locale/pt';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
dayjs.locale('pt');

import { GlobalService } from './global.service';
import { CommonService } from './common.service';

/**
 * UtilityService
 *
 * Service focado em funções utilitárias gerais:
 * - Geração de passwords
 * - Cálculo de idade
 * - Formatação de nomes
 * - BICSWIFT lookup
 * - Configuração de rotas
 */
@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  constructor(
    public globalService: GlobalService,
    private commonService: CommonService,
  ) {}

  /**
   * Gera uma password aleatória forte de 12 caracteres usando CSPRNG
   */
  generatePassword(length = 12): string {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*';
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      const randomValues = new Uint32Array(length);
      window.crypto.getRandomValues(randomValues);
      return Array.from(randomValues, val => alphabet[val % alphabet.length]).join('');
    }
    return Array.from({ length }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('');
  }

  /**
   * Remove a extensão de um filename
   */
  retiraextensao(_type: string, filename): string {
    if (typeof filename !== 'string') return;
    var frags = filename.split('.');
    return frags.splice(0, frags.length - 1).join('.');
  }

  /**
   * Configura visibilidade das rotas baseado nas permissões do utilizador
   */
  configRoutes() {
    // this.routesConfigGlobal = new RoutesConfig();h
    this.globalService.routesConfigGlobal.HideHome = false;
    this.globalService.routesConfigGlobal.HideSimulations = true;
    this.globalService.routesConfigGlobal.HidePolicies = true;
    this.globalService.routesConfigGlobal.HideInsuranceclaims = true;
    this.globalService.routesConfigGlobal.HideContacts = false;

    this.globalService.routesConfigGlobal.HideSitemanagement =
      !this.globalService.loggedUserClass.Admin && !this.globalService.loggedUserClass.SiteManagement;
    this.globalService.routesConfigGlobal.HideCommercialarea =
      !this.globalService.loggedUserClass.Admin &&
      !this.globalService.loggedUserClass.SiteManagement &&
      !this.globalService.loggedUserClass.CommercialArea;
    this.globalService.routesConfigGlobal.HideClientarea =
      !this.globalService.loggedUserClass.Admin &&
      !this.globalService.loggedUserClass.SiteManagement &&
      !this.globalService.loggedUserClass.ClientArea;

    this.globalService.routesConfigGlobal.HideIntermediaryArea =
      !this.globalService.loggedUserClass.Admin &&
      !this.globalService.loggedUserClass.SiteManagement &&
      !this.globalService.loggedUserClass.IntermediaryArea;
  }

  /**
   * Calcula idade a partir de uma data de nascimento
   */
  idade(dateString): string {
    if (!dateString || dateString == '') return '';
    let mdob = dayjs(dateString, 'YYYY-MM-DD');
    let age = dayjs().diff(mdob, 'month');
    let yrs = (age / 12).toFixed(1);
    // let mnths = age % 12;
    return `${parseInt(yrs)}`;
    // const birthday = +new Date(dateString);
    // return ((Date.now() - birthday) / (31557600000));
  }

  /**
   * Extrai primeiro e último nome de um nome completo
   * Remove conectores (de, da, do, etc)
   * Para empresas mantém nome completo
   *
   * @param nome Nome completo
   * @param isEmpresa Flag indicando se é empresa (opcional, default: false)
   * @returns Primeiro e último nome (ou nome completo para empresas)
   */
  PrimeiroUltimoNome(nome: string, isEmpresa: boolean = false): string {
    // Normaliza inputs para evitar crashes em runtime
    const safeName = typeof nome === 'string' ? nome : '';

    // Colapsa múltiplos espaços e trim
    const s = safeName.replace(/\s+/g, ' ').trim();

    // Se vazio, não há nada a fazer
    if (!s) return '';

    // Empresas mantêm o nome completo
    if (isEmpresa) {
      return s;
    }

    // Particiona o nome em tokens (sem vazios)
    const tokens = s.split(' ').filter(Boolean);
    if (tokens.length === 1) return tokens[0]; // nome único

    // Ignora conectores comuns no primeiro/último nome
    const STOP = new Set(['de', 'da', 'do', 'das', 'dos', 'e', "d'", 'del', 'di', 'du']);

    // Primeiro nome útil
    const primeiro = tokens.find(t => !STOP.has(t.toLowerCase())) ?? tokens[0];

    // Último nome útil (varre de trás para a frente)
    let ultimo = tokens[tokens.length - 1];
    for (let i = tokens.length - 1; i >= 0; i--) {
      const t = tokens[i];
      if (!STOP.has(t.toLowerCase())) {
        ultimo = t;
        break;
      }
    }

    // Garante sempre dois termos quando possível
    if (primeiro === ultimo && tokens.length > 1) {
      return `${tokens[0]} ${tokens[tokens.length - 1]}`;
    }

    return `${primeiro} ${ultimo}`.trim();
  }

  /**
   * Verifica se um NIF pertence a uma empresa
   * @param nif Número de Identificação Fiscal
   * @returns true se for empresa, false caso contrário
   */
  isNIFEmpresa(nif: string): boolean {
    if (nif === '' || nif === undefined || !nif) {
      return false;
    }

    return !(nif.toString().charAt(0) === '1' || nif.toString().charAt(0) === '2' || nif.toString().charAt(0) === '3');
  }

  /**
   * Valida se um NIF é válido (algoritmo módulo 11)
   * @param nif Número de Identificação Fiscal
   * @param AdminValidate Se true, ignora validação para NIFs especiais (admin)
   * @returns true se válido
   */
  validateNIF(nif: string, AdminValidate: boolean = false): boolean {
    if (!AdminValidate) {
      if (
        nif === '999999999' ||
        nif === '888888888' ||
        nif === '777777777' ||
        nif === '666666666' ||
        nif === '555555555' ||
        nif === '111111111' ||
        nif === '000000000'
      ) {
        return true;
      }
    }

    if (!nif) {
      return false;
    }

    if (nif.length !== 9) {
      return false;
    }

    if (
      !['1', '2', '3', '5', '6', '8'].includes(nif.slice(0, 1)) &&
      !['45', '70', '71', '72', '77', '79', '90', '91', '98', '99'].includes(nif.slice(0, 2))
    ) {
      return false;
    }

    // Algoritmo de validação do NIF
    const total =
      Number(nif[0]) * 9 +
      Number(nif[1]) * 8 +
      Number(nif[2]) * 7 +
      Number(nif[3]) * 6 +
      Number(nif[4]) * 5 +
      Number(nif[5]) * 4 +
      Number(nif[6]) * 3 +
      Number(nif[7]) * 2;
    const modulo11 = total - Math.trunc(total / 11) * 11;
    const comparador = modulo11 === 1 || modulo11 === 0 ? 0 : 11 - modulo11;
    return Number(nif[8]) === comparador;
  }

  /**
   * Valida um número de Cartão de Cidadão português (algoritmo de Luhn).
   * Aceita formatos com ou sem hífens/espaços (ex: 10890919-7-ZY3 ou 108909197ZY3).
   *
   * O algoritmo converte letras nos seus valores numéricos (A=10, B=11, ..., Z=35)
   * e aplica a validação Luhn sobre os dígitos resultantes.
   *
   * @param ccNumber Número do Cartão de Cidadão
   * @returns true se válido, false caso contrário
   */
  validateCartaoCidadao(ccNumber: string): boolean {
    if (!ccNumber) {
      return false;
    }

    // Remove hífens e espaços
    const cleaned = ccNumber.replace(/[-\s]/g, '').toUpperCase();

    // Deve ter 12 caracteres alfanuméricos (8 dígitos + 1 check digit + 2 letras + 1 check digit)
    if (cleaned.length !== 12) {
      return false;
    }

    // Deve conter apenas letras e números
    if (!/^[A-Z0-9]+$/.test(cleaned)) {
      return false;
    }

    // Mapa de conversão de letras para valores numéricos
    const letterValue: { [key: string]: number } = {
      A: 10,
      B: 11,
      C: 12,
      D: 13,
      E: 14,
      F: 15,
      G: 16,
      H: 17,
      I: 18,
      J: 19,
      K: 20,
      L: 21,
      M: 22,
      N: 23,
      O: 24,
      P: 25,
      Q: 26,
      R: 27,
      S: 28,
      T: 29,
      U: 30,
      V: 31,
      W: 32,
      X: 33,
      Y: 34,
      Z: 35,
    };

    // Converte cada caracter para o(s) seu(s) dígito(s)
    const digits: number[] = [];
    for (const char of cleaned) {
      if (letterValue[char] !== undefined) {
        // Letras são convertidas em dois dígitos (ex: Z=35 → [3, 5])
        const val = letterValue[char];
        digits.push(Math.floor(val / 10));
        digits.push(val % 10);
      } else {
        digits.push(Number(char));
      }
    }

    // Algoritmo de Luhn (da direita para a esquerda)
    digits.reverse();
    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
      let d = digits[i];
      if (i % 2 !== 0) {
        d *= 2;
        if (d >= 10) {
          d -= 9;
        }
      }
      sum += d;
    }

    return sum % 10 === 0;
  }

  /**
   * Atualiza o primeiro/último nome no GlobalService
   * @deprecated Use PrimeiroUltimoNome directly with isEmpresa flag
   */
  onChangeName(fullName: string, _nif: string) {
    // Nota: não conseguimos determinar isEmpresa aqui sem ValidateNifService
    // Assume particular (false) por default
    if (this.globalService.loggedUserClass?.customerClass) {
      this.globalService.loggedUserClass.customerClass.firstLastName = this.PrimeiroUltimoNome(fullName, false);
    }
  }

  /**
   * Obtém código BICSWIFT a partir de um IBAN
   */
  async BICSWIFT(iban: string): Promise<string> {
    return new Promise(resolve => {
      let SWIFT: string;
      SWIFT = '';

      if (iban.length > 4) {
        this.commonService.GetCommonBankCod(iban.substring(0, 4)).subscribe({
          next: dados => (SWIFT = dados[0].BICSWIFT),
          error: error => {
            console.log(error);
            return SWIFT;
          },
          complete: () => {
            resolve(SWIFT);
          },
        });
      }
    });
  }
}
